import { useState } from 'react'

import { ArrowRight01Icon, UserGroupIcon } from '@hugeicons/react'

import {
  MatchedTagList,
  selectTopSuggestions,
  useMatchmakingSuggestionsQuery
} from '@/features/matchmaking'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'

import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'

import type { BlockComponentProps } from '../index'

const DEFAULT_LIMIT = 3

/**
 * Recorte curto das sugestões, com link para o módulo completo.
 *
 * O corte das melhores é feito no cliente sobre a resposta padrão: `per_group`
 * limitaria por grupo, o que devolveria as três melhores de cada tipo em vez das
 * três melhores no geral.
 */
export const MatchmakingBlock = ({ block }: BlockComponentProps) => {
  const { digitalProductId } = useDigitalProductContext()
  const { navigateTo } = useNavigation()
  const [requested, setRequested] = useState(false)
  const { data, isLoading } = useMatchmakingSuggestionsQuery(
    digitalProductId,
    undefined,
    { enabled: requested }
  )

  // Antes da interação o bloco não busca nada: as sugestões são o cálculo mais
  // caro do módulo e a home é a tela mais visitada do evento, então quem abre a
  // home e não olha as conexões não paga por elas.
  if (!requested)
    return (
      <BlockEmptyState
        icon={UserGroupIcon}
        title={block.title || 'Conexões para você'}
        hint="Veja quem no evento declarou interesses em comum com os seus."
        action={
          <Button
            variant="primary"
            className="w-full"
            onClick={() => setRequested(true)}
            trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
          >
            Ver conexões
          </Button>
        }
      />
    )

  if (isLoading) return <BlockSkeleton rows={2} />

  if (data?.cold_start)
    return (
      <BlockEmptyState
        icon={UserGroupIcon}
        title="Conte o que você busca no evento"
        hint="Escolha seus interesses no perfil e passamos a sugerir pessoas, empresas e conteúdos com algo em comum com você."
      />
    )

  const top = selectTopSuggestions(data?.groups, block.settings?.limit ?? DEFAULT_LIMIT)

  if (!top.length)
    return (
      <BlockEmptyState
        icon={UserGroupIcon}
        title="Ainda não encontramos correspondências"
        hint="Assim que alguém do evento declarar os mesmos interesses que você, as sugestões aparecem aqui."
      />
    )

  return (
    <Card className="card-gamification">
      <div className="flex items-center justify-between px-4 pb-0.5 pt-3">
        <h2 className="m-0 flex items-center gap-2 text-caps font-bold uppercase tracking-wide text-gray-700">
          <UserGroupIcon size={14} variant="duotone" aria-hidden />
          {block.title || 'Conexões para você'}
        </h2>
        <Button
          variant="link"
          className="!no-underline text-xs font-bold"
          onClick={() => navigateTo('/conexoes')}
          trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
        >
          Ver todas
        </Button>
      </div>

      <Card.Body className="px-4 pb-3 pt-1.5">
        <ul className="m-0 grid list-none gap-2 p-0">
          {top.map(item => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => navigateTo('/conexoes')}
                className="flex w-full cursor-pointer flex-col items-start gap-1.5 rounded-[0.625rem] border-0 bg-[color-mix(in_srgb,var(--color-brand)_8%,white)] px-3 py-2.5 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-brand)_12%,white)]"
              >
                <span className="text-sm font-semibold text-foreground">
                  {item.name}
                </span>
                <MatchedTagList tags={item.tags} />
              </button>
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  )
}
